import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Globe,
  Package,
  HeartHandshake,
  Search,
  Share2,
  Smartphone,
  Megaphone,
  ArrowLeft,
  FileText,
  ExternalLink,
  ChevronRight,
  BarChart3,
  CheckCircle2,
  Clock,
  AlertCircle,
  Zap,
  Loader2,
  Presentation,
} from "lucide-react";
import { cn, getScoreColor, getScoreLabel, getScoreBgColor, formatDate, SECTION_COLORS } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

const SECTION_ICON_MAP = {
  discover: Globe,
  deliver: Package,
  delight: HeartHandshake,
  dominate: Search,
  discourse: Share2,
  mobile: Smartphone,
  drive: Megaphone,
};

const SECTION_META = {
  discover: { title: "Website Presence & Product Experience", color: "#FACC15" },
  mobile: { title: "Mobile Optimization", color: "#F97316" },
  drive: { title: "Marketing Readiness", color: "#A855F7" },
  deliver: { title: "Post-Purchase & Packaging", color: "#22C55E" },
  delight: { title: "Customer Support", color: "#EC4899" },
  dominate: { title: "Search Visibility (SEO)", color: "#3B82F6" },
  discourse: { title: "Social Footprint", color: "#8B5CF6" },
};

function ScoreCircle({ score, size = 200 }) {
  const radius = (size - 20) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  
  const getStrokeColor = () => {
    if (score >= 75) return "#22C55E";
    if (score >= 50) return "#FACC15";
    return "#EF4444";
  };

  return (
    <div className="score-circle" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          className="score-circle-bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
        />
        <circle
          className="score-circle-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getStrokeColor()}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="score-circle-text">
        <span className={cn("text-5xl font-bold", getScoreColor(score))} style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
          {Math.round(score)}
        </span>
        <span className="text-zinc-500 text-sm">/ 100</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoAuditRunning, setAutoAuditRunning] = useState(false);
  const [auditProgress, setAuditProgress] = useState(null);
  const [generatingPresentation, setGeneratingPresentation] = useState(false);
  const pollIntervalRef = React.useRef(null);
  const pollStartTimeRef = React.useRef(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/", { replace: true });
      return;
    }
    if (!authLoading) fetchAudit();
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
  }, [auditId, authLoading, isAuthenticated]);

  const fetchAudit = async () => {
    try {
      const response = await axios.get(`${API}/audits/${auditId}`);
      setAudit(response.data);
      
      if (response.data.auto_audit_status === "processing" && !response.data.auto_audit_completed) {
        setAutoAuditRunning(true);
        // Restore progress from DB if available
        if (response.data.auto_audit_progress) {
          setAuditProgress(response.data.auto_audit_progress);
        }
        startPolling();
      }
    } catch (error) {
      console.error("Error fetching audit:", error);
      toast.error("Failed to load audit");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const stopPolling = (reason) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollStartTimeRef.current = null;
    setAutoAuditRunning(false);
    setAuditProgress(null);
    if (reason) console.log("Polling stopped:", reason);
  };

  const startPolling = () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollStartTimeRef.current = Date.now();
    let consecutiveErrors = 0;
    
    pollIntervalRef.current = setInterval(async () => {
      // Client-side 6-minute hard timeout
      if (pollStartTimeRef.current && Date.now() - pollStartTimeRef.current > 360000) {
        stopPolling("client timeout");
        toast.error("Auto-audit took too long. Refresh the page to check status.");
        const auditResp = await axios.get(`${API}/audits/${auditId}`);
        setAudit(auditResp.data);
        return;
      }
      
      try {
        const statusResp = await axios.get(`${API}/audits/${auditId}/auto-audit/status`);
        const { status, completed, error, progress } = statusResp.data;
        consecutiveErrors = 0;
        
        if (progress) setAuditProgress(progress);
        
        // Refresh audit data to show incremental section results
        if (progress?.completed_sections?.length > 0) {
          const auditResp = await axios.get(`${API}/audits/${auditId}`);
          setAudit(auditResp.data);
        }
        
        if (status === "completed" && completed) {
          stopPolling("completed");
          toast.success("AI Auto-Audit complete! Review the results.");
          const auditResp = await axios.get(`${API}/audits/${auditId}`);
          setAudit(auditResp.data);
        } else if (status === "failed") {
          stopPolling("failed");
          toast.error(error || "Auto-audit encountered an error. Check the error banner for details.");
          const auditResp = await axios.get(`${API}/audits/${auditId}`);
          setAudit(auditResp.data);
        }
      } catch (pollErr) {
        console.error("Poll error:", pollErr);
        consecutiveErrors++;
        if (consecutiveErrors >= 5) {
          // Instead of giving up, try to refresh the audit state
          // The server likely restarted and reset the stuck audit
          try {
            const auditResp = await axios.get(`${API}/audits/${auditId}`);
            setAudit(auditResp.data);
            // If audit is no longer processing, stop polling
            if (auditResp.data.auto_audit_status !== "processing") {
              stopPolling("server restarted");
              if (auditResp.data.auto_audit_status === "failed") {
                toast.error(auditResp.data.auto_audit_error || "Auto-audit was interrupted. Click Retry.");
              } else if (auditResp.data.auto_audit_completed) {
                toast.success("AI Auto-Audit complete!");
              }
              return;
            }
          } catch (_) {
            // Server truly unreachable
          }
          if (consecutiveErrors >= 10) {
            stopPolling("server unreachable");
            toast.error("Server appears to be restarting. Please refresh the page in a moment.");
          }
        }
      }
    }, 4000);
  };

  const handleAutoAudit = async () => {
    if (autoAuditRunning) return;
    setAutoAuditRunning(true);
    setAuditProgress({ step: 0, total_sections: 7, current_section_name: "Starting...", completed_sections: [] });
    
    try {
      await axios.post(`${API}/audits/${auditId}/auto-audit`);
      startPolling();
    } catch (error) {
      const detail = error.response?.data?.detail || "Auto-audit failed. Please try again.";
      toast.error(detail);
      setAutoAuditRunning(false);
      setAuditProgress(null);
      // Refresh audit to get latest state
      try {
        const auditResp = await axios.get(`${API}/audits/${auditId}`);
        setAudit(auditResp.data);
      } catch (_) {}
    }
  };

  const handleCancelAutoAudit = async () => {
    try {
      await axios.post(`${API}/audits/${auditId}/auto-audit/cancel`);
      stopPolling("user cancelled");
      toast.info("Auto-audit cancelled. You can retry.");
      const auditResp = await axios.get(`${API}/audits/${auditId}`);
      setAudit(auditResp.data);
    } catch (error) {
      toast.error("Failed to cancel. Try refreshing the page.");
    }
  };

  const handleGeneratePresentation = async () => {
    setGeneratingPresentation(true);
    try {
      await axios.post(`${API}/audits/${auditId}/presentation/generate`);
      toast.success("Presentation generated! Opening...");
      // Refresh audit to get presentation data
      const auditResp = await axios.get(`${API}/audits/${auditId}`);
      setAudit(auditResp.data);
      navigate(`/presentation/${auditId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate presentation");
    } finally {
      setGeneratingPresentation(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  if (!audit) return null;

  const progressPercentage = audit.total_questions > 0 
    ? (audit.answered_questions / audit.total_questions) * 100 
    : 0;

  const completedSections = Object.values(audit.sections).filter(
    s => s.answered_questions === s.total_questions && s.total_questions > 0
  ).length;

  return (
    <div className="min-h-screen bg-zinc-950 bg-grid">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-white"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <img src={LOGO_URL} alt="Digital Impressions" className="h-8 w-auto" />
              <div className="hidden sm:block">
                <h1 className="heading-sm text-white">IMPRESS 360 AUDIT</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!audit?.auto_audit_completed && audit?.auto_audit_status !== "processing" && !autoAuditRunning && (
                <Button
                  onClick={handleAutoAudit}
                  disabled={autoAuditRunning}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                  data-testid="auto-audit-btn"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  AI Auto-Audit
                </Button>
              )}
              {!audit?.auto_audit_completed && (autoAuditRunning || audit?.auto_audit_status === "processing") && (
                <div className="flex items-center gap-2" data-testid="auto-audit-progress">
                  <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 animate-pulse px-3 py-1.5">
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    {auditProgress?.current_section_name 
                      ? `Analyzing ${auditProgress.current_section_name} (${auditProgress.step || 0}/${auditProgress.total_sections || 7})`
                      : "AI Analyzing..."
                    }
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelAutoAudit}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs px-2 h-7"
                    data-testid="cancel-auto-audit-btn"
                  >
                    Cancel
                  </Button>
                </div>
              )}
              {audit?.auto_audit_completed && (
                <Badge className="bg-violet-500/20 text-violet-300 border-violet-500/30">
                  <Zap className="w-3 h-3 mr-1" />
                  AI Audited
                </Badge>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/report/${auditId}`)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                data-testid="view-report-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                View Report
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Client Info & Score Row */}
        <div className="grid lg:grid-cols-12 gap-6 mb-8">
          {/* Overall Score */}
          <Card className="lg:col-span-4 card-glass glow-yellow-sm" data-testid="overall-score-card">
            <CardContent className="p-6 flex flex-col items-center justify-center">
              <h2 className="heading-sm text-zinc-400 mb-4">Overall Score</h2>
              <ScoreCircle score={audit.overall_score} />
              <Badge 
                className={cn(
                  "mt-4 px-4 py-1",
                  audit.overall_score >= 75 ? "score-badge-high" :
                  audit.overall_score >= 50 ? "score-badge-medium" : "score-badge-low"
                )}
              >
                {getScoreLabel(audit.overall_score)}
              </Badge>
            </CardContent>
          </Card>

          {/* Client Info */}
          <Card className="lg:col-span-5 card-glass" data-testid="client-info-card">
            <CardHeader>
              <CardTitle className="heading-md text-white">Client Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-zinc-500">Store Name</p>
                  <p className="text-white font-medium">{audit.client_info.store_name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Auditor</p>
                  <p className="text-white font-medium">{audit.client_info.auditor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Website</p>
                  <a 
                    href={audit.client_info.store_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    {audit.client_info.store_url.replace(/^https?:\/\//, '').slice(0, 30)}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <div>
                  <p className="text-sm text-zinc-500">Audit Date</p>
                  <p className="text-white font-medium">{formatDate(audit.client_info.audit_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Progress Stats */}
          <Card className="lg:col-span-3 card-glass" data-testid="progress-card">
            <CardHeader>
              <CardTitle className="heading-md text-white">Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-zinc-400">Questions Answered</span>
                  <span className="text-white font-medium">{audit.answered_questions} / {audit.total_questions}</span>
                </div>
                <Progress value={progressPercentage} className="h-2" />
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {completedSections}
                    </p>
                    <p className="text-xs text-zinc-500">Complete</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {Object.keys(audit.sections).length - completedSections}
                    </p>
                    <p className="text-xs text-zinc-500">Remaining</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Auto-Audit Progress Banner */}
        {!audit?.auto_audit_completed && (autoAuditRunning || audit?.auto_audit_status === "processing") && (
          <Card className="mb-6 card-glass border-violet-500/30" data-testid="auto-audit-progress-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-violet-400" />
                  <span className="heading-sm text-white">AI Auto-Audit in Progress</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-zinc-400">
                    Step {auditProgress.step || 0} of {auditProgress.total_sections || 7}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancelAutoAudit}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs h-7"
                    data-testid="cancel-auto-audit-banner-btn"
                  >
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
              <Progress 
                value={((auditProgress.step || 0) / (auditProgress.total_sections || 7)) * 100} 
                className="h-2 mb-3" 
              />
              <div className="flex flex-wrap gap-2">
                {["DISCOVER", "DISCOVER MOBILE", "DRIVE", "DOMINATE", "DISCOURSE", "DELIVER", "DELIGHT"].map((name, i) => {
                  const sectionIds = ["discover", "mobile", "drive", "dominate", "discourse", "deliver", "delight"];
                  const sid = sectionIds[i];
                  const isCompleted = auditProgress.completed_sections?.includes(sid);
                  const isCurrent = auditProgress.current_section === sid;
                  return (
                    <Badge 
                      key={sid}
                      className={cn(
                        "text-xs transition-all",
                        isCompleted && "bg-green-500/20 text-green-300 border-green-500/30",
                        isCurrent && "bg-amber-500/20 text-amber-300 border-amber-500/30 animate-pulse",
                        !isCompleted && !isCurrent && "bg-zinc-800 text-zinc-500 border-zinc-700"
                      )}
                    >
                      {isCompleted && <CheckCircle2 className="w-3 h-3 mr-1" />}
                      {isCurrent && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                      {name}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Auto-Audit Error Banner */}
        {audit?.auto_audit_status === "failed" && !autoAuditRunning && (
          <Card className="mb-6 card-glass border-red-500/30" data-testid="auto-audit-error-banner">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                  <div>
                    <p className="text-sm text-red-300 font-medium">AI Auto-Audit Failed</p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {audit.auto_audit_error || "An unexpected error occurred during analysis."}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleAutoAudit}
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
                  data-testid="retry-auto-audit-btn"
                >
                  <Zap className="w-3 h-3 mr-1" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section Cards */}
        <h2 className="heading-md text-white mb-6">Audit Sections</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.entries(audit.sections).map(([sectionId, sectionData]) => {
            const Icon = SECTION_ICON_MAP[sectionId];
            const meta = SECTION_META[sectionId];
            const isComplete = sectionData.answered_questions === sectionData.total_questions && sectionData.total_questions > 0;
            const sectionProgress = sectionData.total_questions > 0 
              ? (sectionData.answered_questions / sectionData.total_questions) * 100 
              : 0;

            return (
              <Card
                key={sectionId}
                className={cn(
                  "card-glass card-hover cursor-pointer section-card group",
                  isComplete && "border-green-500/30"
                )}
                style={{ "--section-color": meta.color }}
                onClick={() => navigate(`/audit/${auditId}/${sectionId}`)}
                data-testid={`section-card-${sectionId}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${meta.color}20` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: meta.color }} />
                    </div>
                    {isComplete ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : sectionData.answered_questions > 0 ? (
                      <Clock className="w-6 h-6 text-yellow-500" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-zinc-600" />
                    )}
                  </div>
                  
                  <h3 className="heading-md text-white mb-1">{sectionData.section_name}</h3>
                  <p className="text-sm text-zinc-500 mb-4">{meta.title}</p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Progress</span>
                      <span className="text-white">{sectionData.answered_questions}/{sectionData.total_questions}</span>
                    </div>
                    <Progress value={sectionProgress} className="h-1.5" />
                    
                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-zinc-500" />
                        <span className={cn("font-bold", getScoreColor(sectionData.score))}>
                          {Math.round(sectionData.score)}%
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 flex flex-wrap gap-4">
          {!audit.auto_audit_completed && !autoAuditRunning && audit.auto_audit_status !== "processing" && (
            <Button
              onClick={handleAutoAudit}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
              data-testid="auto-audit-btn-bottom"
            >
              <Zap className="w-4 h-4 mr-2" />
              {audit.auto_audit_status === "failed" ? "Retry AI Auto-Audit" : "AI Auto-Audit"}
            </Button>
          )}
          {audit.auto_audit_status === "failed" && audit.auto_audit_error && !autoAuditRunning && (
            <span className="flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="w-4 h-4" />
              {audit.auto_audit_error}
            </span>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/audits")}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="all-audits-btn"
          >
            All Audits
          </Button>
          {audit.answered_questions > 0 && (
            <Button
              onClick={() => navigate(`/report/${auditId}`)}
              className="btn-primary"
              data-testid="generate-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              View Full Report
            </Button>
          )}
          {audit.answered_questions > 0 && !audit.presentation?.generated && (
            <Button
              onClick={handleGeneratePresentation}
              disabled={generatingPresentation}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white"
              data-testid="generate-presentation-btn"
            >
              {generatingPresentation ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Presentation className="w-4 h-4 mr-2" />
              )}
              {generatingPresentation ? "Generating..." : "Generate Presentation"}
            </Button>
          )}
          {audit.presentation?.generated && (
            <Button
              onClick={() => navigate(`/presentation/${auditId}`)}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white"
              data-testid="view-presentation-btn"
            >
              <Presentation className="w-4 h-4 mr-2" />
              View Presentation
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
